import { EditableTypeType, EditableValue, TemplateManifest } from "hoxton-types";

import { PREDEFINED_MANIFEST_KEYS } from "../enums/manifest";
import { decodeManifest } from "./hoxtonManifest";
import unique from "./unique";

import {
    validateEditables,
    validateHoxtonData,
    validateReportingLabel,
} from "./validateHoxtonManifest";

function getFriendlyName(key: string) {
    return key
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/([a-z])([0-9])/, "$1 $2")
        .replace(/^./, (str) => str.toUpperCase());
}

/**
 * this function assumes the reporting label is valid and does not check
 */
function sanitiseReportingLabel(validReportingLabel: string | undefined, editableKeys: string[]) {
    return validReportingLabel?.replace(/\{(.*?)\}/g, (_, $1: string) => {
        if (!$1) {
            return "";
        }

        if (editableKeys.includes($1)) {
            return `{${$1}}`;
        }

        const lower = $1.toLowerCase();

        // add the braces back
        return `{${lower}}`;
    });
}

function getEditableKeysFromManifest(hoxtonData: TemplateManifest) {
    return Object.keys(hoxtonData).filter((key) => !PREDEFINED_MANIFEST_KEYS.includes(key));
}

function getEditableGroupsFromManifest(hoxtonData: TemplateManifest, editableKeys: string[]) {
    const allEditableGroups = editableKeys.reduce((acc, editableKey) => {
        const editable = hoxtonData[editableKey];

        if (editable.editableGroups) {
            acc.push(
                ...editable.editableGroups.map((editableGroupName) =>
                    editableGroupName.toLowerCase(),
                ),
            );
        }

        acc.push("other fields");

        return acc;
    }, [] as string[]);

    return unique(allEditableGroups);
}

export type ParsedManifestEditable = {
    name: string;
    friendlyName: string;
    defaultValue: EditableValue;
    type: EditableTypeType;
    editableGroups?: string[];
};

function getEditablesFromManifest(manifest: TemplateManifest) {
    const editableNames = getEditableKeysFromManifest(manifest);

    return editableNames.reduce((acc, key) => {
        const friendlyName = getFriendlyName(key);
        const manifestEntry = manifest[key];

        const editable: ParsedManifestEditable = {
            name: key,
            friendlyName,
            defaultValue: manifestEntry.value,
            type: manifestEntry.type,
        };

        if (manifestEntry.editableGroups) {
            editable.editableGroups = manifestEntry.editableGroups.map((editableGroupName) =>
                editableGroupName.toLowerCase(),
            );
        } else {
            editable.editableGroups = ["other fields"];
        }

        acc.push(editable);

        return acc;
    }, [] as ParsedManifestEditable[]);
}

type ParsedManifest = {
    name: string;
    size: { width: number; height: number };
    adType: string;
    backupQuality: number;
    reportingLabel?: string;
    editables: ParsedManifestEditable[];
};

function parseValidatedManifest(hoxtonData: TemplateManifest, editableKeys: string[]) {
    const editables = getEditablesFromManifest(hoxtonData);

    const size = {
        width: parseInt(hoxtonData.adSize.width, 10),
        height: parseInt(hoxtonData.adSize.height, 10),
    };

    const reportingLabel = sanitiseReportingLabel(hoxtonData.reportingLabel, editableKeys);

    const data: ParsedManifest = {
        name: hoxtonData.name,
        size,
        editables,
        adType: hoxtonData.adType,
        backupQuality: parseInt(hoxtonData.backupQuality ?? "80", 10),
        reportingLabel,
    };

    return {
        data,
        success: true,
        messages: ["Parsing successful."],
    };
}

// this will throw on errors and invalid data
export default function parseHoxtonManifest(hoxtonTagContents: string) {
    // Handle both encoded and decoded manifest
    const parsedJSON = decodeManifest(hoxtonTagContents);

    validateHoxtonData(parsedJSON);

    const editableKeys = getEditableKeysFromManifest(parsedJSON);
    const lowerCaseEditableGroups = getEditableGroupsFromManifest(parsedJSON, editableKeys);
    const editableTypes = editableKeys.reduce(
        (acc, key, i) => {
            // we match against lowercased editable names
            acc[editableKeys[i]] = parsedJSON[key].type;

            return acc;
        },
        {} as Record<string, EditableTypeType>,
    );

    validateEditables(parsedJSON, editableKeys);

    validateReportingLabel({
        reportingLabel: parsedJSON.reportingLabel,
        lowerCaseEditableGroups,
        editableNames: editableKeys,
        editableTypes,
    });

    return parseValidatedManifest(parsedJSON, editableKeys);
}

export const forTesting = {
    sanitiseReportingLabel,
    getEditablesFromManifest,
};
